import { useEffect, useRef, useState } from "react";
import { Editor } from "@monaco-editor/react";

import { Code, FullScreenIcon, Play, Terminal, Trash, WindowedScreenIcon } from "../icons/Icons";
import { DropdownInput } from "../form/FormInput";
import { programmingLanguages } from "../utils/constants";
import { postApi } from "../services/apiHelper";
import { codeRun } from "../settings/apiConfig";

import './CodeIDE.scss';

const CodeIDE = ({ value, showLanguageOption, onChange, codeLanguage=null }) => {

    const codeEditorRef = useRef(null);
    const consoleOutputRef = useRef(null);

    const [programmingLanguage, setProgrammingLanguage] = useState(codeLanguage);
    const [isExecuting, setIsExecuting] = useState(false);
    const [output, setOutput] = useState([]);
    const [isFullScreen, setIsFullScreen] = useState(false);

    useEffect(() => {
        scrollToEnd();
    }, [output]);

    useEffect(() => {
        const handleFullscreen = () => {
            const isFullscreen = !!document.fullscreenElement;

            if ( isFullscreen ) {
                setIsFullScreen(true);
            } else {
                setIsFullScreen(false);
            }
        };

        document.addEventListener("fullscreenchange", handleFullscreen);

        return () =>
            document.removeEventListener("fullscreenchange", handleFullscreen);
    }, []);

    const handleEditorDidMount = (editor, monaco) => {
        monaco.editor.defineTheme('custom-dark', {
            base: 'vs-dark',
            inherit: true,
            rules: [
                { token: 'comment', foreground: '6a9955', fontStyle: 'italic' },
                { token: 'keyword', foreground: 'c586c0', fontStyle: 'bold' },
                { token: 'string', foreground: 'ce9178' },
                { token: 'number', foreground: 'b5cea8' },
                { token: 'type', foreground: '4ec9b0' },
                { token: 'function', foreground: 'dcdcaa' },
                { token: 'variable', foreground: '9cdcfe' },
                { token: 'operator', foreground: 'd4d4d4' },
            ],
            colors: {
                'editor.background': '#0d0d0d',
                'editor.lineHighlightBackground': '#1a1a1a',
                'editorCursor.foreground': '#aeafad',
                'editor.selectionBackground': '#264f78',
                'editor.inactiveSelectionBackground': '#3a3d41',
                'editorLineNumber.foreground': '#858585',
                'editorLineNumber.activeForeground': '#c6c6c6',
                'editorIndentGuide.background': '#404040',
                'editorIndentGuide.activeBackground': '#707070',
            }
        });
        monaco.editor.setTheme('custom-dark');
    };
    
    const runCode = async () => {
        setIsExecuting(true);
        const language = programmingLanguage.toLowerCase();
        const logs = [];
        if (language === 'javascript' || language === 'typescript') {
            const originalLog = console.log;
            const originalError = console.error;

            console.log = (...args) => {
                logs.push({ type: 'log', content: args.map(a => String(a)).join(' ') });
            };
            console.error = (...args) => {
                logs.push({ type: 'error', content: args.map(a => String(a)).join(' ') });
            };

            try {
                // Inject input file content if available
                // const inputData = inputFile ? inputFile.content : '';
                // const INPUT_FILE = ${JSON.stringify(inputData)};
        
                // Create a function from the code and execute it
                // We wrap it to provide the input data
                const wrappedCode = `
                    ${value}
                `;
                const fn = new Function(wrappedCode);
                fn();
                logs.push({ type: 'system', content: 'Execution finished successfully.' });
            } catch (err) {
                let errorMessage = 'Unknown error';
                if (err instanceof Error) {
                    errorMessage = err.message;
                } else if (typeof err === 'string') {
                    errorMessage = err;
                } else if (err && typeof err === 'object') {
                    // Handle Pyodide or other object-based errors
                    errorMessage = err.message || err.error?.message || JSON.stringify(err);
                }
                logs.push({ type: 'error', content: errorMessage });
            } finally {
                console.log = originalLog;
                console.error = originalError;
            }
        } else {
            setOutput(prevState => [...prevState, { type: 'compile', content: 'Compiling...' }]);
            
            const response = await validateCode();
            if ( response.data.executionsuccessful ) {
                logs.push({ type: 'system', content: response.data.data });
            } else {
                logs.push({ type: 'error', content: response.data.data });
            }

            setOutput(prevState => [...prevState.filter(elem => elem.type !== 'compile')]);
        }
        
        setOutput(prevState => [...prevState, ...logs]);
        setIsExecuting(false);
    };

    const clearConsole = () => {
        setOutput([]);
    };

    const scrollToEnd = () => {
        if ( consoleOutputRef.current ) {
            consoleOutputRef.current.scrollTo({
                top: consoleOutputRef.current.scrollHeight,
                behavior: "smooth"
            });
        }
    };

    const goFullscreen = async () => {
        try {
            if (!document.fullscreenElement && codeEditorRef.current) {
                await codeEditorRef.current.requestFullscreen();
            }
        } catch (err) {
            console.warn("Enter fullscreen failed:", err);
        }
    };

    const exitFullscreen = async () => {
        try {
            if (document.fullscreenElement && document.exitFullscreen) {
                await document.exitFullscreen();
            }
        } catch (err) {
            console.warn("Exit fullscreen failed:", err);
        }
    };

    const validateCode = async () => {
        try {
            let payload = {
                language: programmingLanguage,
                code: value
            };
            
            return await postApi(codeRun, { data: payload })
        } catch (error) {
            throw error;
        }
    };

    const onInputChange = (value, language) => {
        onChange && onChange(value || '', language);
    };

    return (
        <div className={`code-ide d-flex flex-column flex-grow-1 p-2 p-sm-3 ${isFullScreen ? 'fullscreen-mode' : ''}`} ref={codeEditorRef}>
            <div className="header flex-column flex-sm-row d-flex gap-3 rounded-top">
                <div className="d-flex align-items-center">
                    <div className="code-icon">
                        <Code size={24} />
                    </div>
                    <span className="fw-bold text-white">EDITOR</span>
                </div>

                <div className="d-flex flex-grow-1 align-items-center justify-content-between">
                    {
                        showLanguageOption ? 
                        <div className="p-2" style={{ width: "12rem" }}>
                            <DropdownInput name='programminglanguage' value={programmingLanguage} options={programmingLanguages} onChange={(language) => { 
                                setProgrammingLanguage(language);
                                onInputChange(value, language);
                            }} />    
                        </div> : 
                        <span className="text-n" style={{ color: "white", border: "1px solid #FFF", padding: "0.25rem", borderRadius: "5px", width: "10rem" }}>{codeLanguage}</span>
                    }
                    <div className="d-flex align-items-center gap-3">
                        {
                            isFullScreen ? 
                            <div role="button" onClick={exitFullscreen}>
                                <WindowedScreenIcon color={"#ffffff"} />
                            </div> : 
                            <div role="button" onClick={goFullscreen}>
                                <FullScreenIcon color={"#ffffff"} />
                            </div>
                        }
                        <button className="d-flex align-items-center gap-2 p-1 rounded clickable" style={{ color: "#00d492", border: "1px solid #00d492", backgroundColor: "#05966933" }} onClick={runCode} disabled={isExecuting || !value}>
                            <Play size={16}/> {isExecuting ? "Running..." : "Run"}
                        </button>
                    </div>
                </div>
            </div>

            <div className="code-editor">
                <Editor
                    language={programmingLanguage?.toLowerCase()}
                    theme="custom-dark"
                    value={value}
                    onChange={(codeValue) => onInputChange(codeValue, programmingLanguage)}
                    onMount={handleEditorDidMount}
                    options={{
                        fontSize: 14,
                        lineHeight: 24,
                        letterSpacing: 0.5,
                        minimap: { enabled: false },
                        scrollBeyondLastLine: false,
                        automaticLayout: true,
                        padding: { top: 20 },
                        fontFamily: "'JetBrains Mono', monospace",
                        fontWeight: '400',
                        renderLineHighlight: 'all',
                        cursorBlinking: 'smooth',
                        cursorSmoothCaretAnimation: 'on',
                        smoothScrolling: true,
                        bracketPairColorization: { enabled: true },
                        guides: {
                            bracketPairs: true,
                            indentation: true,
                        }
                    }} 
                />
            </div>
            <div className="console-output rounded-bottom">
                <div className="header d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                        <div className="console-icon">
                            <Terminal size={24} />
                        </div>
                        <span className="fw-bold text-white">CONSOLE OUTPUT</span>
                    </div>
                    <div className="trash-btn" role="button" onClick={clearConsole}>
                        <Trash size={16} />
                    </div>
                </div>

                <div id="output-container" className="output-container d-flex flex-column p-4 gap-3" ref={consoleOutputRef}>
                    {
                        output.length === 0 ? 
                        (
                            <div className="d-flex flex-column flex-grow-1 align-items-center justify-content-center gap-2 font-monospace">
                                <Terminal />
                                <p>No output yet</p>
                            </div>
                        ) : 
                        (
                            output.map((line, i) => (
                                <div className={`output d-flex gap-3 font-monospace ${(line.type == 'system' || line.type == 'compile') && 'text-primary'} ${line.type == 'error' && 'text-danger'}`} key={i}>
                                    <span className="opacity-25 user-select-none">{'>'}</span>
                                    <span className="flex-1 pre-wrap-text">{line.content}</span>
                                </div>
                            ))
                        )
                    }
                </div>
            </div>
        </div>
    )
};

export default CodeIDE;